import { useEffect, useSyncExternalStore } from "react";
import type { CommunityStructure, Conversation, Me, MyCommunity, UserProfile } from "@atrium/shared";
import { queryClient } from "./queryClient";

/*
 * Recently viewed communities, channels, conversations and profiles, kept on this
 * device (localStorage) and surfaced in the quick switcher. Entries are recorded
 * from the URL; names come from data already in the query cache.
 */

export type RecentKind = "community" | "channel" | "conversation" | "profile";

export interface RecentItem {
  kind: RecentKind;
  /** Unique per kind (community/channel/conversation id, username). */
  id: string;
  label: string;
  /** Secondary text, e.g. the community of a channel. */
  sub?: string;
  path: string;
  iconUrl?: string | null;
  at: number;
}

const KEY = "atrium:recent";
const MAX = 30;
const listeners = new Set<() => void>();
let cache: RecentItem[] | null = null;

function read(): RecentItem[] {
  if (cache) return cache;
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    cache = Array.isArray(v) ? (v as RecentItem[]) : [];
  } catch {
    cache = [];
  }
  return cache;
}

function write(items: RecentItem[]) {
  cache = items;
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* storage unavailable */
  }
  listeners.forEach((l) => l());
}

export function recordRecent(item: Omit<RecentItem, "at">) {
  const rest = read().filter((r) => !(r.kind === item.kind && r.id === item.id));
  write([{ ...item, at: Date.now() }, ...rest].slice(0, MAX));
}

export function forgetRecent(kind: RecentKind, id: string) {
  write(read().filter((r) => !(r.kind === kind && r.id === id)));
}

export function clearRecent() {
  write([]);
}

export function useRecent(): RecentItem[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
    read,
  );
}

/* ───────────── Recording from the URL ───────────── */

/** Resolve a path to a recent item using cached data; null when not (yet) known. */
function resolve(pathname: string): Omit<RecentItem, "at"> | null {
  const me = queryClient.getQueryData<Me | null>(["me"]);
  let m = /^\/c\/([0-9a-f-]{36})(?:\/([0-9a-f-]{36}))?/.exec(pathname);
  if (m) {
    const [, communityId, channelId] = m;
    const structure = queryClient.getQueryData<CommunityStructure>(["structure", communityId]);
    const mine = queryClient.getQueryData<MyCommunity[]>(["my-communities"])?.find((c) => c.id === communityId);
    const name = structure?.community.name ?? mine?.name;
    if (!name) return null;
    const iconUrl = structure?.community.iconUrl ?? mine?.iconUrl ?? null;
    if (channelId) {
      const ch = structure?.channels.find((c) => c.id === channelId);
      if (!ch) return null;
      return { kind: "channel", id: channelId, label: ch.name, sub: name, path: `/c/${communityId}/${channelId}`, iconUrl };
    }
    return { kind: "community", id: communityId, label: name, path: `/c/${communityId}`, iconUrl };
  }
  m = /^\/messages\/([0-9a-f-]{36})/.exec(pathname);
  if (m) {
    const c = queryClient.getQueryData<Conversation[]>(["conversations"])?.find((x) => x.id === m![1]) ?? queryClient.getQueryData<Conversation>(["conversation", m[1]]);
    if (!c) return null;
    const others = c.members.filter((u) => u.id !== me?.id);
    const label = c.name || others.map((u) => u.displayName).join(", ") || "Just you";
    return { kind: "conversation", id: c.id, label, sub: c.kind === "group" ? `${c.members.length} people` : others[0] ? `@${others[0].username}` : undefined, path: `/messages/${c.id}`, iconUrl: others.length === 1 ? others[0].avatarUrl : null };
  }
  m = /^\/u\/([^/?#]+)/.exec(pathname);
  if (m) {
    const username = decodeURIComponent(m[1]);
    const p = queryClient.getQueryData<UserProfile>(["profile", username]);
    if (!p || p.username === me?.username) return null;
    return { kind: "profile", id: p.username.toLowerCase(), label: p.displayName, sub: `@${p.username}`, path: `/u/${p.username}`, iconUrl: p.avatarUrl };
  }
  return null;
}

/** Records the current page once its data is in the cache (waits up to 10 s). */
export function useRecordRecent(pathname: string) {
  useEffect(() => {
    if (!/^\/(c\/[0-9a-f-]{36}|messages\/[0-9a-f-]{36}|u\/)/.test(pathname)) return;
    const attempt = () => {
      const item = resolve(pathname);
      if (item) recordRecent(item);
      return !!item;
    };
    if (attempt()) return;
    const unsub = queryClient.getQueryCache().subscribe((e) => {
      if (e.type === "updated" && attempt()) unsub();
    });
    const t = setTimeout(unsub, 10_000);
    return () => {
      unsub();
      clearTimeout(t);
    };
  }, [pathname]);
}
